import { getContentLanguage } from "../../common/content_language";
import { arrayContainsArray } from "../../common/utils";

// Locale-aware lowercase so Turkish "İ"/"I" fold correctly (tr: İ→i, I→ı) while
// English keeps default behavior (I→i). Plain toLowerCase() turns "İ" into
// "i"+combining-dot, which never matches a typed "i". Folds by the training language.
const lc = (s) => (s || "").toString().toLocaleLowerCase(getContentLanguage());

// A plugin checkAnswer result is a boolean or a 0..1 score fraction
// (allowPartialScore). Gates that need "fully correct" (waitForCorrectAction,
// requiredCorrectAnswers) must not accept a partial fraction — partial credit
// earns score, not correctness.
export function isFullyCorrect(result) {
  if (result === true) return true;
  const n = parseFloat(result);
  return !isNaN(n) && n >= 1;
}

export function correctLiterally(current, correct) {
  return JSON.stringify(current) === JSON.stringify(correct) ? 1 : 0;
}

export function correctArrayUnordered(current, correct, allowPartial, totalPossibleAnswers) {
  if (!current && correct && correct.length === 0) {
    return 1;
  }
  let correctSanitized = correct.filter((c) => {
    return c < totalPossibleAnswers;
  });
  if (allowPartial) {
    let score = 0;
    if (Array.isArray(current) && Array.isArray(correctSanitized)) {
      for (let i = 0; i < totalPossibleAnswers; i++) {
        let isCorrect = correctSanitized.indexOf(i) === -1 && current.indexOf(i) === -1;
        isCorrect = isCorrect || (correctSanitized.indexOf(i) > -1 && current.indexOf(i) > -1);
        score = score + (isCorrect ? 1 : 0);
      }
      return score / (totalPossibleAnswers || 1);
    }
  } else if (Array.isArray(current) && Array.isArray(correctSanitized)) {
    return (
      (arrayContainsArray(correctSanitized, current) &&
        current.length === correctSanitized.length) ||
      (current.length === 0 && correctSanitized.length === 0)
    );
  }

  return 0;
}

export function correctArrayOrdered(current, correct, totalAnswers, allowPartial = false) {
  let total = Math.min(totalAnswers || 1, correct ? correct.length : 1);
  let score = 0;
  for (let q in current) {
    if (current[q] === correct[q]) {
      score += 1;
    }
  }
  let finalScore = score / total;
  return !allowPartial && finalScore < 1 ? 0 : finalScore;
}

export function compareNumbersLiterally(current, correct) {
  if (!isNaN(current)) {
    return current === correct ? 1 : 0;
  }
  return 0;
}

export function correctNumericInput(current, correct, tol = 0) {
  let user = current;
  let answer = correct;
  let precision = tol;
  try {
    user = parseFloat(user);
    answer = parseFloat(answer);
    precision = parseFloat(precision);
    if (isNaN(user) || isNaN(answer) || isNaN(precision)) {
      return 0;
    }
  } catch (e) {
    return 0;
  }

  if (user <= answer + precision && user >= answer - precision) {
    return 1;
  }
  return 0;
}

function sanitize(str) {
  let accents = "ÀÁÂÃÄÅàáâãäåßÒÓÔÕÕÖØòóôõöøÈÉÊËèéêëðÇçÐÌÍÎÏìíîïÙÚÛÜùúûüÑñŠšŸÿýŽž";
  let accentsOut = "AAAAAAaaaaaaBOOOOOOOooooooEEEEeeeeeCcDIIIIiiiiUUUUuuuuNnSsYyyZz";
  str = str.split("");
  str.forEach((letter, index) => {
    let i = accents.indexOf(letter);
    if (i !== -1) {
      str[index] = accentsOut[i];
    }
  });
  return str.join("");
}

export function correctTextInput(current, correct, strictCorrection) {
  let answers = correct && correct.split ? correct.split("//") : [""];
  let user = current;
  let sanitizedAnswers = answers;
  if (!strictCorrection) {
    sanitizedAnswers = answers.map((an) => {
      return sanitize(lc(an));
    });
    user = sanitize(lc(current));
  }
  if (sanitizedAnswers.indexOf(user) > -1) {
    return 1;
  }
  return 0;
}

export function correctLongAnswer(current, correct, strictCorrection) {
  // No length shortcut: a long model answer must still be matched, not
  // auto-passed (scoring bug #2). The "no model answer / participation" case is
  // handled upstream in FreeResponse.checkAnswer via state.correct === false.
  if (strictCorrection) {
    return (current || "").toString() === (correct || "").toString() ? 1 : 0;
  }
  return sanitize(lc(current)) === sanitize(lc(correct)) ? 1 : 0;
}

// FillInBlank scorer (scoring bug #5): every blank must match, comparison is
// accent/case-insensitive (via sanitize), each blank accepts "//"-separated
// alternates, an empty learner blank fails, and a zero-blank question does NOT
// auto-pass.
export function correctFillInBlank(current, correct, allowPartial = false) {
  if (!Array.isArray(current) || !Array.isArray(correct) || correct.length === 0) {
    return false;
  }
  if (current.length !== correct.length) {
    return false;
  }
  // Count matched blanks. When allowPartial is on, return the fraction of blanks
  // filled correctly; otherwise keep the all-or-nothing boolean (an empty or
  // wrong blank fails the whole question).
  let matched = 0;
  for (let i = 0; i < correct.length; i++) {
    const learner = sanitize(lc(current[i]).trim());
    if (learner.length === 0) {
      continue;
    }
    const accepted = (correct[i] || "")
      .toString()
      .split("//")
      .map((a) => sanitize(lc(a).trim()));
    if (accepted.indexOf(learner) !== -1) {
      matched++;
    }
  }
  if (allowPartial) {
    return matched / correct.length;
  }
  return matched === correct.length;
}
